import type React from 'react';

import { Badge } from '~/components/ui/Badge';
import { Container } from '~/components/ui/Container';
import { Reveal } from '~/components/ui/Reveal';
import { TechGrid } from '~/components/ui/TechGrid';
import { cn } from '~/lib/cn';
import { useContent } from '~/hooks/useContent';

/**
 * Certification — a compact banner section indicating certification status.
 * Uses `bg-deep-panel` with a TechGrid overlay to provide visual separation.
 * Content is centred; badges use `variant="status"` (Steel Mist outline).
 */
function Certification(): React.ReactElement {
  const content = useContent();
  const { certification } = content;

  return (
    <section
      id="certification"
      className={cn(
        'relative overflow-hidden',
        'bg-[var(--color-deep-panel)]',
        'py-20 md:py-28',
        'scroll-anchor'
      )}
      aria-labelledby="certification-heading"
    >
      {/* TechGrid background overlay */}
      <TechGrid className="absolute inset-0" />

      <Container className="relative z-10">
        <Reveal>
          <div className="flex flex-col items-center text-center gap-6 max-w-2xl mx-auto">
            {/* Heading */}
            <h2
              id="certification-heading"
              className={cn(
                'text-4xl md:text-5xl font-display font-bold tracking-[0.03em] leading-tight',
                'text-[var(--color-frost-white)]'
              )}
            >
              {certification.heading}
            </h2>

            {/* Body */}
            <p
              className={cn(
                'text-base font-body font-light leading-[1.8]',
                'text-[var(--color-steel-mist)]',
                'max-w-[640px]'
              )}
            >
              {certification.body}
            </p>

            {/* Status badges row */}
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {certification.badges.map((badge) => (
                <Badge key={badge} variant="status">
                  {badge}
                </Badge>
              ))}
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export { Certification };
export default Certification;
